import { Controller } from "@hotwired/stimulus"

// Brand palette for the stacked token components + cost line.
const COLORS = {
  input: "#6851d6",
  output: "#ea473c",
  cached_read: "#24cb72",
  cached_write: "#e8b84b",
  cost: "#342d26",
}

// Categorical palette for the model / source breakdowns.
const CATEGORICAL = [
  "#6851d6", "#ea473c", "#24cb72", "#e8b84b", "#3b9ae1",
  "#d6519e", "#8a8f98", "#e8772e", "#1f9e8f", "#9b6dd6",
]

function abbrev(value) {
  const n = Number(value) || 0
  const abs = Math.abs(n)
  if (abs >= 1e9) return (n / 1e9).toFixed(1) + "B"
  if (abs >= 1e6) return (n / 1e6).toFixed(1) + "M"
  if (abs >= 1e3) return (n / 1e3).toFixed(1) + "K"
  return String(Math.round(n))
}

function money(value) {
  const n = Number(value) || 0
  if (n >= 1) return "$" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  if (n > 0) return "$" + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })
  return "$0.00"
}

// Renders the admin AI-usage charts from JSON payloads embedded by the view.
// Chart.js is loaded via a CDN <script> in the page <head>.
export default class extends Controller {
  static values = { series: Object, models: Object, sources: Object }
  static targets = ["trendCanvas", "modelsCanvas", "sourcesCanvas", "measureButton"]

  connect() {
    this.measure = "tokens"
    this.charts = {}
    this.whenChartReady(() => window.requestAnimationFrame(() => this.renderAll()))
  }

  disconnect() {
    Object.values(this.charts).forEach((chart) => chart && chart.destroy())
    this.charts = {}
  }

  whenChartReady(callback, attempts = 0) {
    if (typeof window.Chart !== "undefined") return callback()
    if (attempts > 50) return console.warn("[admin-ai-usage] Chart.js did not load")
    setTimeout(() => this.whenChartReady(callback, attempts + 1), 100)
  }

  renderAll() {
    this.renderTrend()
    this.renderModels()
    this.renderSources()
    this.updateMeasureButtons()
  }

  // Tokens vs. cost toggle — only the model + source breakdowns switch measure;
  // the trend always shows token components with the cost line overlay.
  setMeasure(event) {
    const next = event.currentTarget.dataset.measure
    if (!next || next === this.measure) return
    this.measure = next
    this.renderModels()
    this.renderSources()
    this.updateMeasureButtons()
  }

  updateMeasureButtons() {
    this.measureButtonTargets.forEach((button) => {
      button.classList.toggle("btn-neutral", button.dataset.measure === this.measure)
    })
  }

  draw(slot, canvas, config) {
    if (this.charts[slot]) this.charts[slot].destroy()
    canvas.style.display = "block"
    canvas.style.width = "100%"
    canvas.style.height = "100%"
    this.charts[slot] = new window.Chart(canvas.getContext("2d"), config)
    window.requestAnimationFrame(() => this.charts[slot]?.resize())
  }

  renderTrend() {
    const s = this.seriesValue
    const stacked = (label, data, color) => ({
      type: "bar", label, data, backgroundColor: color, stack: "tokens", yAxisID: "y", order: 1,
    })

    this.draw("trend", this.trendCanvasTarget, {
      data: {
        labels: s.labels,
        datasets: [
          stacked("Input", s.input, COLORS.input),
          stacked("Output", s.output, COLORS.output),
          stacked("Cached read", s.cached_read, COLORS.cached_read),
          stacked("Cached write", s.cached_write, COLORS.cached_write),
          {
            type: "line", label: "Est. cost", data: s.cost,
            borderColor: COLORS.cost, backgroundColor: COLORS.cost,
            borderWidth: 2, tension: 0.3, pointRadius: 2, pointHoverRadius: 4,
            yAxisID: "yCost", order: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } } },
          tooltip: {
            callbacks: {
              label: (ctx) => ctx.dataset.yAxisID === "yCost"
                ? `${ctx.dataset.label}: ${money(ctx.parsed.y)}`
                : `${ctx.dataset.label}: ${Number(ctx.parsed.y).toLocaleString()}`,
            },
          },
        },
        scales: {
          x: { stacked: true, grid: { display: false } },
          y: {
            stacked: true, position: "left", beginAtZero: true,
            title: { display: true, text: "Tokens" },
            ticks: { callback: (v) => abbrev(v) },
          },
          yCost: {
            position: "right", beginAtZero: true,
            title: { display: true, text: "Est. cost (USD)" },
            grid: { drawOnChartArea: false },
            ticks: { callback: (v) => "$" + abbrev(v) },
          },
        },
      },
    })
  }

  renderModels() {
    const d = this.modelsValue
    const data = this.measure === "cost" ? d.cost : d.tokens
    this.draw("models", this.modelsCanvasTarget, {
      type: "doughnut",
      data: { labels: d.labels, datasets: [{ data, backgroundColor: CATEGORICAL, borderWidth: 0 }] },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "62%",
        plugins: {
          legend: { position: "right", labels: { usePointStyle: true, boxWidth: 8, font: { size: 11 } } },
          tooltip: { callbacks: { label: (ctx) => this.shareLabel(ctx) } },
        },
      },
    })
  }

  renderSources() {
    const d = this.sourcesValue
    const isCost = this.measure === "cost"
    const data = isCost ? d.cost : d.tokens
    this.draw("sources", this.sourcesCanvasTarget, {
      type: "bar",
      data: {
        labels: d.labels,
        datasets: [{
          label: isCost ? "Est. cost" : "Tokens",
          data,
          backgroundColor: isCost ? COLORS.cost : COLORS.input,
          borderRadius: 4,
        }],
      },
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => isCost ? money(ctx.parsed.x) : Number(ctx.parsed.x).toLocaleString(),
            },
          },
        },
        scales: {
          x: { grid: { display: false }, ticks: { callback: (v) => isCost ? "$" + abbrev(v) : abbrev(v) } },
          y: { grid: { display: false } },
        },
      },
    })
  }

  shareLabel(ctx) {
    const total = ctx.dataset.data.reduce((sum, n) => sum + Number(n), 0) || 1
    const pct = (Number(ctx.parsed) / total * 100).toFixed(1)
    const formatted = this.measure === "cost" ? money(ctx.parsed) : Number(ctx.parsed).toLocaleString()
    return `${ctx.label}: ${formatted} (${pct}%)`
  }
}
